import React from 'react';

/**
 * Button 컴포넌트
 *
 * @param {string} variant - primary, secondary, ghost, danger
 * @param {string} size - sm, md, lg
 * @param {boolean} disabled - 비활성화
 * @param {boolean} loading - 로딩 상태
 * @param {string} icon - 아이콘 (이모지 또는 문자)
 * @param {boolean} iconOnly - 아이콘만 표시
 * @param {boolean} fullWidth - 전체 너비
 * @param {function} onClick - 클릭 핸들러
 * @param {React.ReactNode} children - 버튼 내용
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon,
  iconOnly = false,
  fullWidth = false,
  onClick,
  children,
  type = 'button',
  className = '',
  ...props
}) => {
  const handleClick = (e) => {
    if (!disabled && !loading && onClick) {
      onClick(e);
    }
  };

  const getVariantStyle = () => {
    switch (variant) {
      case 'primary':
        return 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700';
      case 'secondary':
        return 'bg-white text-gray-900 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 active:bg-gray-200';
      case 'ghost':
        return 'bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200';
      case 'danger':
        return 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700';
      default:
        return 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700';
    }
  };

  const getSizeStyle = () => {
    if (iconOnly) {
      switch (size) {
        case 'sm': return 'w-7 h-7 p-0 text-xs rounded-md';
        case 'lg': return 'w-11 h-11 p-0 text-sm rounded-lg';
        default: return 'w-9 h-9 p-0 text-[13px] rounded-lg';
      }
    }
    switch (size) {
      case 'sm': return 'h-7 px-3 text-xs rounded-md';
      case 'lg': return 'h-11 px-6 text-sm rounded-lg';
      default: return 'h-9 px-4 text-[13px] rounded-lg';
    }
  };

  return (
    <button
      type={type}
      className={`
        inline-flex items-center justify-center gap-1.5 border-none font-medium cursor-pointer transition-all duration-150 whitespace-nowrap relative
        focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-opacity-30
        disabled:opacity-50 disabled:cursor-not-allowed
        ${getVariantStyle()}
        ${getSizeStyle()}
        ${fullWidth ? 'w-full' : ''}
        ${loading ? 'pointer-events-none' : ''}
        ${className}
      `}
      onClick={handleClick}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <span className={`border-2 border-transparent border-t-current rounded-full animate-spin ${size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5'}`}></span>
      )}
      {icon && !loading && <span className="text-[1em] leading-none">{icon}</span>}
      {!iconOnly && children && <span>{children}</span>}
    </button>
  );
};

/**
 * IconButton 컴포넌트 - 아이콘만 있는 버튼
 */
export const IconButton = ({
  icon,
  variant = 'ghost',
  size = 'md',
  tooltip,
  ...props
}) => {
  return (
    <Button
      variant={variant}
      size={size}
      icon={icon}
      iconOnly
      title={tooltip}
      aria-label={tooltip}
      {...props}
    />
  );
};

/**
 * ButtonGroup 컴포넌트 - 버튼 그룹
 */
export const ButtonGroup = ({ children, className = '' }) => {
  return (
    <div className={`inline-flex gap-2 ${className}`}>
      {children}
    </div>
  );
};

export default Button;
